/**
 * Safely extract a human-readable message from an unknown error.
 *
 * Handles the three shapes we see in practice:
 *  - axios errors, where the server message lives at `error.response.data.message`
 *  - plain / network / timeout errors, which only expose `error.message`
 *  - anything else -> the provided fallback
 *
 * Reading `error.data.message` directly (as some older handlers did) throws a
 * TypeError on network/timeout errors that have no `.data`, which discards the
 * real error and leaves cleanup code (e.g. setLoading(false)) unreached.
 */
export const getErrorMessage = (
  error: unknown,
  fallback = 'Something went wrong.',
): string => {
  const err = error as {
    response?: { data?: { message?: unknown } };
    message?: unknown;
  } | null;

  const responseMessage = err?.response?.data?.message;
  if (typeof responseMessage === 'string' && responseMessage) {
    return responseMessage;
  }

  const errorMessage = err?.message;
  if (typeof errorMessage === 'string' && errorMessage) {
    return errorMessage;
  }

  return fallback;
};
